import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { rupees, formatIndian, purityColor, formatApiError } from "@/lib/utils";
import { toast } from "sonner";
import { Undo2, Trash2, Inbox } from "lucide-react";

export default function Trash() {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/products-trash").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const restore = async (jc) => {
    try {
      await api.post(`/products/${encodeURIComponent(jc)}/restore`);
      toast.success(`${jc} restored`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const permanent = async (jc) => {
    if (!window.confirm(`Permanently delete ${jc}? This cannot be undone.`)) return;
    try {
      await api.delete(`/products/${encodeURIComponent(jc)}/permanent`);
      toast.success(`${jc} permanently deleted`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="trash-page">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] mb-2">Catalogue</p>
        <h1 className="font-serif text-4xl md:text-5xl text-[#0D1B2A]">Trash</h1>
        <p className="text-slate-500 mt-2">Soft-deleted products. Restore or permanently delete.</p>
      </div>

      {items === null ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => <div key={i} className="skeleton h-16" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="luxe-card p-16 text-center">
          <Inbox className="w-10 h-10 mx-auto text-[#C9A84C] mb-3" strokeWidth={1} />
          <h3 className="font-serif text-2xl text-[#0D1B2A]">Trash is empty</h3>
          <p className="text-slate-500 text-sm mt-1">Deleted products will appear here.</p>
        </div>
      ) : (
        <div className="luxe-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0D1B2A]/3">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-6 py-4">Jewel Code</th>
                <th className="px-6 py-4">Style</th>
                <th className="px-6 py-4">Category</th>
                <th className="px-6 py-4">Purity</th>
                <th className="px-6 py-4 text-right">Net Wt</th>
                <th className="px-6 py-4">Deleted on</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.jewel_code} className="border-t border-[#0D1B2A]/5" data-testid={`trash-row-${p.jewel_code}`}>
                  <td className="px-6 py-4 font-medium text-[#0D1B2A]">{p.jewel_code}</td>
                  <td className="px-6 py-4 text-slate-600">{p.style_no}</td>
                  <td className="px-6 py-4 text-slate-600">{p.category}</td>
                  <td className="px-6 py-4"><span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border ${purityColor(p.purity)}`}>{p.purity}</span></td>
                  <td className="px-6 py-4 text-right tabular-nums">{formatIndian(p.net_weight, { decimals: 3 })} g</td>
                  <td className="px-6 py-4 text-xs text-slate-500">{p.deleted_at ? new Date(p.deleted_at).toLocaleString() : "—"}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="inline-flex gap-2">
                      <Button onClick={() => restore(p.jewel_code)} variant="outline" size="sm" className="border-[#0D1B2A]/15" data-testid={`restore-${p.jewel_code}`}><Undo2 className="w-4 h-4 mr-1" /> Restore</Button>
                      <Button onClick={() => permanent(p.jewel_code)} variant="outline" size="sm" className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`permanent-delete-${p.jewel_code}`}><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-slate-400">Tip: products are kept indefinitely in Trash until you permanently delete them.</p>
    </div>
  );
}
